/**
 * DriverWallet — Payout-P3 UI (driver self)
 * Consumes:
 *   GET  /api/driver/wallet
 *   GET  /api/driver/withdrawals
 *   POST /api/driver/withdrawals
 *   POST /api/driver/withdrawals/{id}/cancel
 */
import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  ArrowLeft, Wallet, Clock, CheckCircle2, Ban, Info,
  Loader2, X, AlertTriangle,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const MIN_WITHDRAWAL = 1500;

const fmtJMD = (v) =>
  new Intl.NumberFormat('en-JM', { style: 'currency', currency: 'JMD', minimumFractionDigits: 0 }).format(v || 0);

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-JM', { dateStyle: 'medium', timeStyle: 'short' }); } catch { return iso; }
};

const STATUS_BADGE = {
  requested: { label: 'Requested', cls: 'bg-yellow-100 text-yellow-800' },
  approved:  { label: 'Approved',  cls: 'bg-blue-100 text-blue-800' },
  paid:      { label: 'Paid',      cls: 'bg-green-100 text-green-800' },
  rejected:  { label: 'Rejected',  cls: 'bg-red-100 text-red-700' },
  cancelled: { label: 'Cancelled', cls: 'bg-gray-100 text-gray-700' },
};

export default function DriverWallet() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [err, setErr] = useState(null);

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }), [token]);

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const [wr, hr] = await Promise.all([
        fetch(`${API_URL}/api/driver/wallet`, { headers: authHeaders }),
        fetch(`${API_URL}/api/driver/withdrawals?limit=20`, { headers: authHeaders }),
      ]);
      if (!wr.ok) throw new Error('wallet load failed');
      if (!hr.ok) throw new Error('withdrawals load failed');
      const wd = await wr.json();
      const hd = await hr.json();
      setWallet(wd.wallet);
      setWithdrawals(hd.withdrawals || []);
    } catch (e) { setErr(e.message); } finally { setLoading(false); }
  };

  useEffect(() => { if (token) load(); /* eslint-disable-next-line */ }, [token]);

  const openRequest = withdrawals.find(w => ['requested', 'approved'].includes(w.status));
  const canWithdraw = wallet && wallet.available_jmd >= MIN_WITHDRAWAL && !openRequest;

  const cancelRequest = async (id) => {
    if (!window.confirm('Cancel this withdrawal request?')) return;
    try {
      const r = await fetch(`${API_URL}/api/driver/withdrawals/${id}/cancel`, { method: 'POST', headers: authHeaders });
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.detail || 'cancel failed'); }
      toast.success('Cancelled');
      load();
    } catch (e) { toast.error(e.message); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-green-700" />
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 pb-24" data-testid="driver-wallet-page">
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(-1)} data-testid="driver-wallet-back"><ArrowLeft className="w-5 h-5" /></button>
          <div>
            <h1 className="text-lg font-semibold">Earnings Wallet</h1>
            <p className="text-xs text-gray-500">
              {wallet?.driver_type === 'LIANTIAO_OWNED'
                ? 'Commission only — salary handled via payroll'
                : 'Commission from deliveries'}
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 pt-5 space-y-5">
        {err && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{err}</div>}

        {/* Balance cards */}
        <section className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="driver-wallet-balances">
          <BalanceCard
            testId="wallet-available"
            icon={<Wallet className="w-5 h-5 text-green-700" />}
            label="Available"
            value={fmtJMD(wallet?.available_jmd)}
            sub={openRequest ? `JM$${(wallet?.outstanding_withdrawal_reserve_jmd || 0).toLocaleString()} reserved` : 'Ready to withdraw'}
            tone="green"
          />
          <BalanceCard
            testId="wallet-pending"
            icon={<Clock className="w-5 h-5 text-amber-600" />}
            label="Pending (7-day hold)"
            value={fmtJMD(wallet?.pending_jmd)}
            sub={wallet?.next_release_date ? `Next release ${fmtDate(wallet.next_release_date)}` : 'No pending earnings'}
            tone="amber"
          />
          <BalanceCard
            testId="wallet-current"
            icon={<CheckCircle2 className="w-5 h-5 text-blue-700" />}
            label="Current balance"
            value={fmtJMD(wallet?.current_balance_jmd)}
            sub={`Lifetime earned ${fmtJMD(wallet?.lifetime_earned_jmd)}`}
            tone="blue"
          />
        </section>

        {/* 7-day hold explainer */}
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm flex gap-3" data-testid="wallet-hold-explainer">
          <Info className="w-5 h-5 text-blue-700 shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-blue-900">7-day hold policy</p>
            <p className="text-blue-800 mt-1">
              Every delivery commission sits in <span className="font-semibold">pending</span> for 7 days before moving to
              <span className="font-semibold"> available</span>. This protects both of us against disputes and reversals. Only
              <span className="font-semibold"> available</span> funds can be withdrawn.
            </p>
          </div>
        </div>

        {/* Withdraw action */}
        <div className="rounded-xl bg-white border p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div>
            <p className="font-semibold">Withdraw earnings</p>
            <p className="text-sm text-gray-600">Minimum {fmtJMD(MIN_WITHDRAWAL)} · one open request at a time</p>
            {openRequest && (
              <p className="text-xs text-yellow-700 mt-1 flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5" /> You already have an open request ({STATUS_BADGE[openRequest.status]?.label})
              </p>
            )}
            {!openRequest && wallet && wallet.available_jmd < MIN_WITHDRAWAL && (
              <p className="text-xs text-gray-500 mt-1">You need at least {fmtJMD(MIN_WITHDRAWAL)} available to request</p>
            )}
          </div>
          <Button
            data-testid="driver-wallet-withdraw-button"
            disabled={!canWithdraw}
            onClick={() => setShowModal(true)}
            className="bg-green-700 hover:bg-green-800 disabled:bg-gray-300"
          >
            Request withdrawal
          </Button>
        </div>

        {/* Withdrawal history */}
        <section data-testid="driver-wallet-history">
          <h2 className="font-semibold mb-2">Withdrawal history</h2>
          {withdrawals.length === 0 ? (
            <div className="rounded-lg border bg-white p-6 text-center text-sm text-gray-500">No withdrawal requests yet</div>
          ) : (
            <div className="rounded-lg border bg-white divide-y">
              {withdrawals.map(w => (
                <div key={w.id} className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2" data-testid={`withdrawal-row-${w.id}`}>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{fmtJMD(w.approved_amount || w.requested_amount)}</span>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_BADGE[w.status]?.cls}`}>{STATUS_BADGE[w.status]?.label}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Requested {fmtDate(w.requested_at)}
                      {w.paid_at && ` · Paid ${fmtDate(w.paid_at)}`}
                      {w.reject_reason && ` · Reason: ${w.reject_reason}`}
                    </p>
                    {w.payment_reference && <p className="text-xs text-gray-500">Ref: {w.payment_reference}</p>}
                  </div>
                  {w.status === 'requested' && (
                    <Button
                      variant="ghost" size="sm"
                      className="text-red-600"
                      onClick={() => cancelRequest(w.id)}
                      data-testid={`cancel-withdrawal-${w.id}`}
                    ><Ban className="w-4 h-4 mr-1" />Cancel</Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      </main>

      {showModal && (
        <WithdrawModal
          wallet={wallet}
          onClose={() => setShowModal(false)}
          onSubmitted={() => { setShowModal(false); load(); }}
          authHeaders={authHeaders}
        />
      )}
    </div>
  );
}

function BalanceCard({ icon, label, value, sub, tone = 'green', testId }) {
  const border = { green: 'border-green-200', amber: 'border-amber-200', blue: 'border-blue-200' }[tone];
  return (
    <div className={`rounded-xl bg-white border ${border} p-4`} data-testid={testId}>
      <div className="flex items-center gap-2 text-sm text-gray-600">{icon} {label}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
      <div className="text-xs text-gray-500 mt-1">{sub}</div>
    </div>
  );
}

function WithdrawModal({ wallet, onClose, onSubmitted, authHeaders }) {
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('bank_transfer');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  const amt = parseFloat(amount) || 0;
  const maxAmt = wallet?.available_jmd || 0;
  const tooLow = amt > 0 && amt < MIN_WITHDRAWAL;
  const tooHigh = amt > maxAmt;
  const ok = amt >= MIN_WITHDRAWAL && amt <= maxAmt;

  const submit = async () => {
    if (!ok) return;
    setSubmitting(true); setErr(null);
    try {
      const r = await fetch(`${API_URL}/api/driver/withdrawals`, {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({ amount: amt, payment_method: method, notes: notes || undefined }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.detail || 'Request failed');
      toast.success('Withdrawal requested — awaiting admin review');
      onSubmitted();
    } catch (e) { setErr(e.message); toast.error(e.message); } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-4" data-testid="withdraw-modal">
      <div className="bg-white rounded-xl w-full max-w-md shadow-xl">
        <div className="p-5 border-b flex items-center justify-between">
          <h3 className="font-semibold">Request withdrawal</h3>
          <button onClick={onClose} data-testid="withdraw-modal-close"><X className="w-5 h-5 text-gray-500" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="rounded-lg bg-green-50 border border-green-200 p-3 text-sm">
            <div className="text-green-800">Available: <span className="font-semibold">{fmtJMD(maxAmt)}</span></div>
            <div className="text-xs text-green-700">Minimum {fmtJMD(MIN_WITHDRAWAL)}</div>
          </div>

          <div>
            <label className="text-sm font-medium">Amount (JMD)</label>
            <input
              type="number" inputMode="decimal" min={MIN_WITHDRAWAL} max={maxAmt} step={100}
              value={amount} onChange={(e) => setAmount(e.target.value)}
              className="mt-1 w-full rounded border px-3 py-2 focus:ring-2 focus:ring-green-500 focus:outline-none"
              data-testid="withdraw-amount-input"
              placeholder={`${MIN_WITHDRAWAL}`}
            />
            {tooLow && <p className="text-xs text-red-600 mt-1">Below minimum ({fmtJMD(MIN_WITHDRAWAL)})</p>}
            {tooHigh && <p className="text-xs text-red-600 mt-1">Above available balance ({fmtJMD(maxAmt)})</p>}
          </div>

          <div>
            <label className="text-sm font-medium">Payment method</label>
            <div className="mt-1 grid grid-cols-2 gap-2">
              {[
                { value: 'bank_transfer', label: 'Bank transfer' },
                { value: 'cash_office',   label: 'Cash office' },
              ].map(opt => (
                <button key={opt.value}
                  type="button"
                  onClick={() => setMethod(opt.value)}
                  data-testid={`withdraw-method-${opt.value}`}
                  className={`rounded border px-3 py-2 text-sm ${method === opt.value ? 'border-green-600 bg-green-50 text-green-800' : 'border-gray-300 text-gray-700'}`}
                >{opt.label}</button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-sm font-medium">Notes (optional)</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} maxLength={500}
              className="mt-1 w-full rounded border px-3 py-2 focus:ring-2 focus:ring-green-500 focus:outline-none"
              data-testid="withdraw-notes-input"
            />
          </div>

          {err && <div className="text-sm text-red-600">{err}</div>}
        </div>
        <div className="p-5 border-t flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={submitting} data-testid="withdraw-cancel-button">Cancel</Button>
          <Button
            onClick={submit}
            disabled={!ok || submitting}
            className="bg-green-700 hover:bg-green-800"
            data-testid="withdraw-submit-button"
          >{submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit request'}</Button>
        </div>
      </div>
    </div>
  );
}
